(() => {
  'use strict';
  const configNode = document.getElementById('bstore-catalog-search-config');
  if (!configNode) return;
  const config = JSON.parse(configNode.textContent);
  const text = config.text;
  const normalize = value => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
  const tokensFor = value => normalize(value).split(/\s+/).filter(Boolean).map(word => {
    if (word.length > 4 && word.endsWith('ies')) return word.slice(0, -3) + 'y';
    if (/(sses|ches|shes|xes|zes)$/.test(word)) return word.slice(0, -2);
    return word.length > 3 && word.endsWith('s') && !word.endsWith('ss') ? word.slice(0, -1) : word;
  });
  const indexProduct = product => ({ ...product, titleTokens: tokensFor(product.title), tokens: tokensFor(`${product.title} ${product.text}`) });
  const scoreProduct = (product, query) => {
    const terms = tokensFor(query);
    if (!terms.length || !terms.every(term => product.tokens.some(token => token.startsWith(term)))) return 0;
    const title = normalize(product.title);
    return (title === normalize(query) ? 1000 : 0) + (title.includes(normalize(query)) ? 100 : 0) + terms.reduce((score, term) => score + (product.titleTokens.some(token => token.startsWith(term)) ? 15 : 1), 0);
  };
  let catalogPromise;
  let catalogAt = 0;
  const pageUrl = page => {
    const url = new URL(config.catalogUrl, location.origin);
    url.searchParams.set('view', 'bstore-search-index');
    url.searchParams.set('page', page);
    url.searchParams.set('sort_by', 'title-ascending');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  const readPage = async page => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(pageUrl(page).toString(), { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Catalog unavailable');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const dataNode = html.getElementById('bstore-catalog-page');
      if (!dataNode) throw new Error('Catalog unavailable');
      const data = JSON.parse(dataNode.textContent);
      if (data.version !== 1 || data.page !== page || !Array.isArray(data.products) || !Number.isInteger(data.pages) || data.pages > 250) throw new Error('Invalid catalog');
      return data;
    } finally { clearTimeout(timeout); }
  };
  const getCatalog = () => {
    if (!catalogPromise || Date.now() - catalogAt > 60000) {
      catalogAt = Date.now();
      catalogPromise = (async () => {
        const first = await readPage(1);
        const pages = new Array(Math.max(1, first.pages));
        pages[0] = first.products;
        let nextPage = 2;
        await Promise.all(Array.from({ length: Math.min(3, Math.max(0, first.pages - 1)) }, async () => {
          while (nextPage <= first.pages) {
            const page = nextPage++;
            const data = await readPage(page);
            if (data.total !== first.total || data.pages !== first.pages) throw new Error('Catalog changed');
            pages[page - 1] = data.products;
          }
        }));
        const products = pages.flat();
        const unique = new Map(products.map(product => [product.id, product]));
        if (unique.size !== first.total) throw new Error('Incomplete catalog');
        return [...unique.values()].map(indexProduct);
      })().catch(error => { catalogPromise = null; throw error; });
    }
    return catalogPromise;
  };
  const searchCatalog = async query => (await getCatalog()).map(product => ({ product, score: scoreProduct(product, query) })).filter(result => result.score > 0).sort((a, b) => b.score - a.score || a.product.title.localeCompare(b.product.title)).map(result => result.product);
  const searchUrl = query => {
    const url = new URL(config.searchUrl, location.origin);
    url.searchParams.set('q', query);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url;
  };
  const makeCard = (product, compact = false) => {
    const card = document.createElement(compact ? 'a' : 'article');
    card.className = compact ? 'search-item' : 'bstore-search-card';
    card.dataset.productId = product.id;
    const link = compact ? card : document.createElement('a');
    link.href = product.url;
    link.setAttribute('aria-label', product.title);
    const thumbnail = document.createElement('div');
    thumbnail.className = 'thumbnail';
    if (product.image) {
      const img = document.createElement('img');
      img.src = product.image;
      img.alt = product.title;
      img.width = 534;
      img.height = 801;
      img.loading = 'lazy';
      thumbnail.append(img);
    }
    link.append(thumbnail);
    const content = document.createElement('div');
    content.className = 'content';
    const title = document.createElement(compact ? 'span' : 'h2');
    title.className = 'item-title';
    title.textContent = product.title;
    content.append(title);
    if (compact) link.append(content);
    else { link.append(content); card.append(link); }
    const price = document.createElement('div');
    price.className = 'bstore-search-current-price';
    price.innerHTML = product.priceHtml;
    (compact ? content : card).append(price);
    if (!product.available) {
      const stock = document.createElement('p');
      stock.className = 'bstore-search-stock';
      stock.textContent = text.soldOut;
      (compact ? content : card).append(stock);
    }
    return card;
  };
  const countText = (count, query) => (count === 1 ? text.result : text.results).replace('__COUNT__', count).replace('__QUERY__', query);
  class BstoreSearchDrawer extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.input = this.querySelector('[data-js-search-input]');
      this.results = this.querySelector('[data-js-search-results]');
      this.status = document.createElement('p');
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.dataset.bstoreSearchStatus = '';
      this.results.before(this.status);
      this.input.setAttribute('aria-controls', this.results.id);
      this.revision = 0;
      this.input.addEventListener('input', () => {
        clearTimeout(this.timer);
        const revision = ++this.revision;
        const query = this.input.value.trim();
        this.results.replaceChildren();
        this.results.removeAttribute('aria-busy');
        this.status.textContent = query ? text.loading : '';
        this.timer = setTimeout(() => this.search(query, revision), 250);
      });
      this.input.addEventListener('keydown', event => {
        if (event.key === 'ArrowDown') {
          const first = this.results.querySelector('a');
          if (first) { event.preventDefault(); first.focus(); }
        }
      });
      this.results.addEventListener('keydown', event => {
        const links = [...this.results.querySelectorAll('a')];
        const position = links.indexOf(document.activeElement);
        if (position < 0) return;
        if (event.key === 'ArrowDown') { event.preventDefault(); links[Math.min(position + 1, links.length - 1)].focus(); }
        if (event.key === 'ArrowUp') { event.preventDefault(); if (position === 0) this.input.focus(); else links[position - 1].focus(); }
      });
    }
    async search(query, revision) {
      if (!query) return;
      this.results.setAttribute('aria-busy', 'true');
      try {
        const matches = await searchCatalog(query);
        if (revision !== this.revision) return;
        this.results.replaceChildren(...matches.slice(0, 6).map(product => makeCard(product, true)));
        this.status.textContent = matches.length ? countText(matches.length, query) : text.empty;
        if (matches.length) {
          const all = document.createElement('a');
          all.className = 'button button--solid bstore-search-all';
          all.href = searchUrl(query);
          all.textContent = (matches.length === 1 ? text.viewOne : text.viewAll).replace('__COUNT__', matches.length);
          this.results.append(all);
        }
      } catch (error) {
        this.status.dataset.searchError = error.message;
        if (revision === this.revision) {
          this.results.replaceChildren();
          this.status.textContent = text.error;
          const browse = document.createElement('a');
          browse.href = config.catalogUrl;
          browse.textContent = text.browse;
          this.results.append(browse);
        }
      } finally { if (revision === this.revision) this.results.removeAttribute('aria-busy'); }
    }
  }
  if (!customElements.get('bstore-search-drawer')) customElements.define('bstore-search-drawer', BstoreSearchDrawer);
  const initializePage = () => {
    const page = document.querySelector('[data-bstore-search-page]');
    if (!page || page.dataset.initialized) return;
    page.dataset.initialized = 'true';
    const form = page.querySelector('form');
    const input = form.querySelector('input[name=q]');
    const status = page.querySelector('[data-bstore-search-status]');
    const grid = page.querySelector('[data-bstore-search-grid]');
    const more = page.querySelector('[data-bstore-search-more]');
    const controls = page.querySelector('[data-bstore-search-controls]');
    const sort = page.querySelector('[data-bstore-search-sort]');
    let results = [];
    let visible = 0;
    let revision = 0;
    const appendPage = () => {
      const next = results.slice(visible, visible + 24).map(product => makeCard(product));
      grid.append(...next);
      visible += next.length;
      more.hidden = visible >= results.length;
      return next;
    };
    const paint = () => {
      if (sort.value === 'title') results.sort((a, b) => a.title.localeCompare(b.title));
      else results.sort((a, b) => scoreProduct(b, input.value) - scoreProduct(a, input.value) || a.title.localeCompare(b.title));
      visible = 0;
      grid.replaceChildren();
      appendPage();
    };
    const run = async (updateUrl = false) => {
      const current = ++revision;
      const query = input.value.trim();
      results = [];
      grid.replaceChildren();
      more.hidden = true;
      controls.hidden = true;
      if (updateUrl) history.pushState({}, '', searchUrl(query));
      if (!query) { grid.removeAttribute('aria-busy'); status.textContent = text.prompt; return; }
      status.textContent = text.loading;
      grid.setAttribute('aria-busy', 'true');
      try {
        const matches = await searchCatalog(query);
        if (current !== revision) return;
        results = matches;
        status.textContent = matches.length ? countText(matches.length, query) : text.empty;
        controls.hidden = !matches.length;
        paint();
      } catch (error) {
        status.dataset.searchError = error.message;
        if (current === revision) {
          status.textContent = text.error;
          const browse = document.createElement('a');
          browse.href = config.catalogUrl;
          browse.textContent = text.browse;
          grid.append(browse);
        }
      } finally { if (current === revision) grid.removeAttribute('aria-busy'); }
    };
    form.addEventListener('submit', event => { event.preventDefault(); run(true); });
    more.addEventListener('click', () => { const next = appendPage(); const link = next[0]?.querySelector('a'); if (link) link.focus(); });
    sort.addEventListener('change', paint);
    window.addEventListener('popstate', () => { input.value = new URL(location.href).searchParams.get('q') || ''; run(); });
    run();
  };
  initializePage();
  document.addEventListener('shopify:section:load', initializePage);
})();
